// =============================================================
//  ProfileScreenAdmin — Page de profil admin (lien vers édition,
//  déconnexion, infos rapides).
// =============================================================

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { signOut } from 'firebase/auth';
import { auth } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Avatar, Divider } from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

export default function ProfileScreenAdmin({ navigation }) {
  const { user } = useAuth();
  const name = user?.fullName || user?.email?.split('@')[0] || 'Administrateur';

  const handleLogout = async () => {
    if (!confirm('Se déconnecter du tableau de bord ?')) return;
    try {
      await signOut(auth);
    } catch (e) {
      console.log('logout error', e?.message || e);
    }
  };

  return (
    <AdminLayout title="Mon profil" subtitle="Informations administrateur">
      <View style={styles.row}>
        <Card style={{ flex: 2, minWidth: 320, gap: spacing.md }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
            <Avatar src={user?.photoURL} name={name} size={80} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.name}>{name}</Text>
              <Text style={styles.email}>{user?.email || '—'}</Text>
              <View style={{ flexDirection: 'row', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <Badge variant="primary">Administrateur</Badge>
                {!!user?.adminLevel && <Badge variant="info">{user.adminLevel}</Badge>}
              </View>
            </View>
          </View>

          <Divider />

          <View style={styles.grid}>
            <Info label="Email" value={user?.email} />
            <Info label="Téléphone" value={user?.phone || user?.phoneNumber} />
            <Info label="Ville" value={user?.city} />
            <Info label="Niveau" value={user?.adminLevel || 'level2'} />
          </View>

          <Divider />

          <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
            <Button
              icon="account-cog-outline"
              onPress={() => navigation.navigate('EditProfileScreenAdmin')}
            >
              Modifier mon profil
            </Button>
            <Button
              variant="secondary"
              icon="logout"
              onPress={handleLogout}
            >
              Déconnexion
            </Button>
          </View>
        </Card>

        <Card style={{ flex: 1, minWidth: 280, gap: spacing.md, height: 'fit-content' }}>
          <Text style={styles.section}>Accès rapide</Text>
          <Button
            variant="ghost"
            icon="view-dashboard-outline"
            fullWidth
            onPress={() => navigation.navigate('TabNavigatorAdmin')}
          >
            Tableau de bord
          </Button>
          <Button
            variant="ghost"
            icon="account-group-outline"
            fullWidth
            onPress={() => navigation.navigate('ManageUsersScreen')}
          >
            Gérer les utilisateurs
          </Button>
          <Button
            variant="ghost"
            icon="file-document-multiple-outline"
            fullWidth
            onPress={() => navigation.navigate('ManageDocumentsScreen')}
          >
            Gérer les documents
          </Button>
          {user?.adminLevel === 'level1' && (
            <Button
              variant="ghost"
              icon="account-key-outline"
              fullWidth
              onPress={() => navigation.navigate('AddAdmin')}
            >
              Nouvel administrateur
            </Button>
          )}
        </Card>
      </View>
    </AdminLayout>
  );
}

function Info({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} numberOfLines={1}>{value || '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', gap: spacing.lg, flexWrap: 'wrap', alignItems: 'flex-start' },
  name: { fontSize: 22, fontWeight: '700', color: colors.text },
  email: { fontSize: 13, color: colors.textSecondary, marginTop: 4 },
  section: { fontSize: 14, fontWeight: '700', color: colors.text },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase' },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },
});
